import { useEffect, useRef } from 'react';

// Define a type for our particle for type safety
interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    alpha: number;
    update: () => void;
    draw: (ctx: CanvasRenderingContext2D) => void;
}

// Component for the Waterfall Stream Animation
export const WaterfallStream = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const particlesRef = useRef<Particle[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;

        const resizeHandler = () => {
            canvas.width = container.offsetWidth;
            canvas.height = container.offsetHeight;
        };

        resizeHandler(); // Set initial size
        window.addEventListener('resize', resizeHandler);

        class ParticleImpl implements Particle {
            x: number;
            y: number;
            vx: number;
            vy: number;
            radius: number;
            alpha: number;

            constructor(x: number, y: number) {
                this.x = x;
                this.y = y;
                this.vx = (Math.random() - 0.5) * 0.5;
                this.vy = -(Math.random() * 1.5 + 0.3); // Constant slower upward velocity
                this.radius = Math.random() * 2.5 + 1;
                this.alpha = 1;
            }

            update() {
                this.x += this.vx;
                this.y += this.vy; // Will now move upward since vy is negative
                this.alpha -= 0.003;
            }

            draw(ctx: CanvasRenderingContext2D) {
                ctx.globalAlpha = this.alpha;
                ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Create new particles at the bottom of the screen
            for (let i = 0; i < 5; i++) {
                if (particlesRef.current.length < 500) { // Limit particle count
                    particlesRef.current.push(new ParticleImpl(
                        Math.random() * canvas.width,
                        canvas.height - 50 + Math.random() * 50 // Start from bottom of screen
                    ));
                }
            }

            // Update and draw particles
            particlesRef.current.forEach((p, i) => {
                p.update();
                p.draw(ctx);
                if (p.alpha <= 0 || p.y < 0) { // Remove particles when they go off the top of screen
                    particlesRef.current.splice(i, 1);
                }
            });

            ctx.globalAlpha = 1; // Reset alpha
            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeHandler);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    // --- CHANGE 1: Swapped 'absolute' for 'fixed' to attach to the viewport
    // --- and added z-1 to place it between the wave (z-0) and content (z-10)
    return <div ref={containerRef} className="fixed top-0 left-0 w-full h-full overflow-hidden z-1 pointer-events-none"><canvas ref={canvasRef} className="opacity-40" /></div>;
};


// Dynamic Water Background Component
export const WaterBackground = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        let width = (canvas.width = window.innerWidth);
        let height = (canvas.height = window.innerHeight);
        let frame = 0;
        let animationFrameId: number;

        const resizeHandler = () => {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
        };

        window.addEventListener('resize', resizeHandler);

        const draw = () => {
            ctx.clearRect(0, 0, width, height);
            frame++;
            const waveHeight = 20;
            const waveFrequency = 0.005;
            const waveSpeed = 0.015;

            for (let i = 0; i < 3; i++) {
                ctx.beginPath();
                ctx.moveTo(0, height);
                for (let x = 0; x < width; x++) {
                    const y = Math.sin(x * waveFrequency + frame * waveSpeed + i * 2) * waveHeight * (i + 1) * 0.5 + height * 0.85;
                    ctx.lineTo(x, y);
                }
                ctx.lineTo(width, height);
                ctx.closePath();
                ctx.fillStyle = `rgba(255, 255, 255, ${0.1 + i * 0.05})`;
                ctx.fill();
            }
            animationFrameId = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            window.removeEventListener('resize', resizeHandler);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-0 pointer-events-none" />;
};
